import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import PaymentReceipt from '@/components/PaymentReceipt';
import TriageDrawer from '@/components/TriageDrawer';
import { todayIST } from '@/utils/date';
import WalkinSessionPanel from './WalkinSessionPanel';
import WalkinRegistrationForm from './WalkinRegistrationForm';
import WalkinQueuePanel from './WalkinQueuePanel';
import useWalkinForm from './useWalkinForm';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: API, withCredentials: true });

/** Reception walk-in desk: sessions + sectioned form + session queue. */
export default function ReceptionWalkinDesk({ onBack }) {
  const { user } = useAuth();
  const today = todayIST();
  const [date, setDate] = useState(today);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [queue, setQueue] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [receipt, setReceipt] = useState(null);
  const [triageEntry, setTriageEntry] = useState(null);

  const refreshQueue = useCallback(async (sessionId) => {
    if (!sessionId) { setQueue([]); return; }
    try {
      const r = await ax.get(`/api/clinic-ops/queue?session_id=${sessionId}`);
      setQueue(r.data || []);
    } catch (_) { setQueue([]); }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, dRes] = await Promise.all([
        ax.get(`/api/clinic-ops/sessions?date=${date}`),
        ax.get('/api/clinic-ops/doctors'),
      ]);
      setSessions(sRes.data || []);
      setDoctors(dRes.data || []);
      setSelectedSession((prev) => {
        if (prev && (sRes.data || []).some((s) => s.id === prev.id)) return prev;
        return (sRes.data || [])[0] || null;
      });
    } catch (_) {}
    setLoading(false);
  }, [date]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { refreshQueue(selectedSession?.id); }, [selectedSession?.id, refreshQueue]);

  const form = useWalkinForm({
    selectedSession,
    doctors,
    onCheckedIn: (rcpt) => {
      if (rcpt) setReceipt({ ...rcpt, collected_by: user?.email });
      refreshQueue(selectedSession?.id);
      refresh();
    },
  });

  const docName = (id) => doctors.find((d) => d.id === id)?.name || '—';

  return (
    <div data-testid="reception-walkin-desk">
      {onBack ? (
        <div className="mb-4 flex items-center justify-between gap-3">
          <button type="button" onClick={onBack}
            className="text-sm font-semibold text-[#071B34] hover:text-[#F15A22]"
            data-testid="walkin-back">
            ← Back to appointments
          </button>
          <p className="text-xs text-[#5A6B7C]">Walk-in registration · sectioned form</p>
        </div>
      ) : null}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
        <div className="xl:col-span-3">
          <WalkinSessionPanel
            date={date} setDate={setDate} sessions={sessions} loading={loading}
            selectedSession={selectedSession} setSelectedSession={setSelectedSession}
            doctors={doctors} docName={docName} user={user} onRefresh={refresh}
          />
        </div>
        <div className="xl:col-span-6">
          <WalkinRegistrationForm form={form} selectedSession={selectedSession} docName={docName} />
        </div>
        <div className="xl:col-span-3">
          <WalkinQueuePanel
            queue={queue} selectedSession={selectedSession} setQueue={setQueue}
            onTriage={setTriageEntry}
          />
        </div>
      </div>

      {receipt && <PaymentReceipt data={receipt} onClose={() => setReceipt(null)} />}
      <TriageDrawer
        open={!!triageEntry}
        queueEntry={triageEntry}
        onClose={() => setTriageEntry(null)}
        onSaved={() => {
          refreshQueue(selectedSession?.id);
          setTriageEntry(null);
        }}
      />
    </div>
  );
}
